import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { i18nService } from '../../services/i18n';

type ToolInfo = { name: string; description?: string; inputSchema: Record<string, unknown> };
/** 显示 Host 实际发现的工具与参数；检查只执行 MCP 握手和 tools/list，不执行业务工具。 */
export default function ConnectorToolsDialog({ slug, title, onClose }: { slug: string; title: string; onClose: () => void }) {
  const [tools, setTools] = useState<ToolInfo[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const t = (key: string) => i18nService.t(`connectorTools${key}`);
  useEffect(() => {
    let current = true;
    setTools(null); setFailed(false);
    /** 浏览器只访问本站接口；上游凭据、地址检查与 MCP 会话留在服务端。 */
    const inspect = async () => {
      try {
        const result = await window.electron.cowork.getConnectorTools?.(slug);
        if (!result?.success || !Array.isArray(result.tools)) throw new Error('Unavailable');
        if (current) setTools(result.tools);
      } catch { if (current) setFailed(true); }
    };
    void inspect();
    return () => { current = false; };
  }, [slug, revision]);
  useEffect(() => {
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key);
  }, [onClose]);
  return createPortal(<div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
    <section role="dialog" aria-modal="true" aria-label={t('Title')} onClick={event => event.stopPropagation()}
      className="w-full max-w-xl max-h-[80vh] overflow-y-auto rounded-2xl bg-claude-surface dark:bg-claude-darkSurface p-5 space-y-3 text-claude-text dark:text-claude-darkText">
      <div className="flex items-center justify-between gap-3"><h2 className="font-semibold break-all">{title} · {t('Title')}</h2>
        <button autoFocus type="button" onClick={onClose} aria-label={i18nService.t('close')}>×</button></div>
      <p className="text-xs opacity-70">{t('Hint')}</p>
      {tools === null && !failed && <p role="status">{t('Loading')}</p>}
      {(failed || tools?.length === 0) && <p role="alert" className="text-sm text-amber-600">{t('Unavailable')}</p>}
      {tools?.map((tool, index) => <details key={`${tool.name}:${index}`} className="rounded-lg border border-claude-border dark:border-claude-darkBorder p-3">
        <summary className="cursor-pointer text-sm font-medium break-all">{tool.name}</summary>
        <p className="text-xs whitespace-pre-wrap break-words mt-2">{tool.description}</p>
        <pre className="max-h-56 overflow-auto rounded bg-black/5 dark:bg-white/5 p-2 mt-2 text-xs">{JSON.stringify(tool.inputSchema, null, 2)}</pre>
      </details>)}
      <button type="button" disabled={tools === null && !failed} onClick={() => setRevision(value => value + 1)} className="text-sm text-claude-accent disabled:opacity-50">{t('Retry')}</button>
    </section>
  </div>, document.body);
}
